import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { supabase } from '../integrations/supabase/client';

interface UserContextType {
  users: User[];
  currentUser: User | null;
  addUser: (user: User) => void;
  updateUser: (user: User) => void;
  deleteUser: (id: string) => void;
  getUserById: (id: string) => User | undefined;
  login: (email: string, password: string) => User | null;
  logout: () => void;
  changePassword: (userId: string, newPassword: string) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Carregar usuários do Supabase
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Erro ao carregar usuários:', error);
          return;
        }

        // Converter dados do Supabase para o formato User
        const convertedUsers: User[] = data?.filter(profile => profile.user_id !== null).map(profile => ({
          id: profile.user_id as string, // CORRIGIDO: usar user_id em vez de id
          firstName: profile.first_name,
          lastName: profile.last_name,
          nickname: `${profile.first_name} ${profile.last_name}`,
          email: profile.email,
          phone: profile.phone || '',
          role: profile.role,
          password: '', // Não armazenamos senhas localmente
          isFirstLogin: profile.force_password_change,
          createdAt: new Date(profile.created_at),
          updatedAt: new Date(profile.updated_at)
        })) || [];

        console.log('👥 UserContext - Usuários carregados:', convertedUsers);
        console.log('📞 UserContext - Telefones dos usuários:', convertedUsers.map(u => ({ id: u.id, name: u.firstName + ' ' + u.lastName, phone: u.phone })));

        setUsers(convertedUsers);
      } catch (error) {
        console.error('Erro ao buscar usuários:', error);
      }
    };

    fetchUsers();
  }, []);

  const addUser = async (user: User) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          first_name: user.firstName,
          last_name: user.lastName,
          email: user.email,
          phone: user.phone,
          role: user.role || 'corretor',
          is_active: true,
          force_password_change: true
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao adicionar usuário:', error);
        throw error;
      }

      if (!data.user_id) {
        throw new Error('User ID não encontrado no perfil criado');
      }

      const newUser: User = {
        id: data.user_id, // CORRIGIDO: usar user_id em vez de id
        firstName: data.first_name,
        lastName: data.last_name,
        nickname: `${data.first_name} ${data.last_name}`,
        email: data.email,
        phone: data.phone || '',
        role: data.role,
        password: '',
        isFirstLogin: data.force_password_change,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };

      setUsers(prev => [...prev, newUser]);
    } catch (error) {
      console.error('Erro ao adicionar usuário:', error);
      throw error;
    }
  };

  const updateUser = async (updatedUser: User) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: updatedUser.firstName,
          last_name: updatedUser.lastName,
          email: updatedUser.email,
          phone: updatedUser.phone,
          role: updatedUser.role,
          force_password_change: updatedUser.isFirstLogin,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', updatedUser.id); // CORRIGIDO: usar user_id em vez de id

      if (error) {
        console.error('Erro ao atualizar usuário:', error);
        throw error;
      }

      setUsers(prev => 
        prev.map(user => 
          user.id === updatedUser.id ? { ...updatedUser, updatedAt: new Date() } : user
        )
      );
      
      if (currentUser?.id === updatedUser.id) {
        setCurrentUser({ ...updatedUser, updatedAt: new Date() });
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      throw error;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: false })
        .eq('user_id', id); // CORRIGIDO: usar user_id em vez de id

      if (error) {
        console.error('Erro ao deletar usuário:', error);
        throw error;
      }

      setUsers(prev => prev.filter(user => user.id !== id));
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      throw error;
    }
  };

  const getUserById = (id: string) => {
    const user = users.find(user => user.id === id);
    if (user) {
      console.log(`📞 getUserById - Usuário ${user.firstName} ${user.lastName} - Telefone: ${user.phone}`);
    }
    return user;
  };

  // Login agora é apenas um placeholder - a autenticação real é feita pelo Supabase Auth
  const login = (email: string, _password: string) => {
    const user = users.find(u => u.email === email);
    if (user) {
      setCurrentUser(user);
      return user;
    }
    return null;
  };

  const logout = () => {
    setCurrentUser(null);
  };

  const changePassword = async (userId: string, _newPassword: string) => {
    try {
      // A mudança de senha real seria feita via Supabase Auth
      // Aqui apenas atualizamos o flag de primeiro login
      const { error } = await supabase
        .from('profiles')
        .update({ 
          force_password_change: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId); // CORRIGIDO: usar user_id em vez de id

      if (error) {
        console.error('Erro ao atualizar flag de primeiro login:', error);
        throw error;
      }

      setUsers(prev => 
        prev.map(user => 
          user.id === userId 
            ? { ...user, isFirstLogin: false, updatedAt: new Date() }
            : user
        )
      );
      
      if (currentUser?.id === userId) {
        setCurrentUser(prev => prev ? { ...prev, isFirstLogin: false } : null);
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      throw error;
    }
  };

  const value = {
    users,
    currentUser,
    addUser,
    updateUser,
    deleteUser,
    getUserById,
    login,
    logout,
    changePassword
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};